import React from 'react';
import { Box, Divider } from '@drone-js/core';
import { connect } from 'react-redux';
import { mapDispatchToProps, mapStateToProps, ReviewCardProps, ReviewListProps } from 'component/page/detail/Review/Review.selectors';
import ReviewCard from 'component/page/detail/Review/ReviewCard/ReviewCard';
import ReviewPagination from 'component/page/detail/Review/ReviewPagination/ReviewPagination';
import ReviewSectionPlaceHolder from 'component/page/detail/Review/ReviewSection/ReviewSectionPlaceHolder';
import ErrorContent from 'component/page/error/ErrorContent';
import ContentArea from 'component/common/ContentArea/ContentArea';
import { reload } from 'core-framework/window/location';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesLoadFailedClickEventActivitiesFieldsV1,
    ActivitiesLoadFailedSeenEventActivitiesFieldsV1,
    ActivitiesReviewNoResultSeenEventActivitiesFieldsV1,
    ActivitiesReviewsListSeenEventActivitiesFieldsV1,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesLoadFailedClickEventActivitiesManifestV1,
    ActivitiesLoadFailedSeenEventActivitiesManifestV1,
    ActivitiesReviewNoResultSeenEventActivitiesManifestV1,
    ActivitiesReviewsListSeenEventActivitiesManifestV1,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { getSelectedFiltersType } from 'component/page/detail/Review/ReviewSortAndFilter/ReviewSortAndFilter.utils';
import { InView } from 'react-intersection-observer';

const ReviewCardList: React.FC<{ reviewList: ReviewCardProps[]; isInOverlay?: boolean }> = (props) => {
    const onSeen = (inView: boolean) => {
        if (inView) {
            trackElementEvent<ActivitiesReviewsListSeenEventActivitiesFieldsV1>(ActivitiesReviewsListSeenEventActivitiesManifestV1.ids, {});
        }
    };

    return (
        <InView onChange={onSeen} triggerOnce={true} threshold={1}>
            <div data-element-name="activities-review-list" data-testid="activities-review-list" data-nosnippet="true">
                {props.reviewList.map((review, index) => (
                    <ReviewCard key={review.id} index={index} review={review} isInOverlay={props.isInOverlay} />
                ))}
            </div>
        </InView>
    );
};
ReviewCardList.displayName = 'ReviewCardList';

ReviewCardList.defaultProps = {
    isInOverlay: false,
};

const ReviewList: React.FC<ReviewListProps> = (props) => {
    const { onClearFilterCreator, activityId, cms } = props;
    const onClearFilter = React.useMemo(() => onClearFilterCreator(activityId), [activityId, onClearFilterCreator]);
    const isSuccessLoaded = !props.isLoading && props.isSuccess;
    const isError = !props.isLoading && !props.isSuccess;

    React.useEffect(() => {
        if (isError) {
            trackElementEvent<ActivitiesLoadFailedSeenEventActivitiesFieldsV1>(ActivitiesLoadFailedSeenEventActivitiesManifestV1.ids, {});
        }
    }, [isError]);

    if (isSuccessLoaded && (!props.reviewList || props.reviewList.length === 0)) {
        if (props.currentFilter.valueFilters.length > 0) {
            trackElementEvent<ActivitiesReviewNoResultSeenEventActivitiesFieldsV1>(ActivitiesReviewNoResultSeenEventActivitiesManifestV1.ids, {
                selected_filters: getSelectedFiltersType(props.currentFilter).join(','),
            });
            return (
                <ContentArea
                    data-element-name="activities-review-no-result"
                    data-testid="activities-review-no-result"
                    py="16"
                    px="24"
                    display="flex"
                    justifyContent="center"
                    flexDirection="column"
                    max={60}
                    m="auto"
                >
                    <ErrorContent
                        onClick={onClearFilter}
                        assetName="no_result_found.svg"
                        assetWidth="240px"
                        buttonText={props.cms[252532]}
                        errorTitleText=""
                        errorDescriptionText={props.cms[277312]}
                    />
                </ContentArea>
            );
        }
        return null;
    }

    const onReloadButtonClick = () => {
        trackElementEvent<ActivitiesLoadFailedClickEventActivitiesFieldsV1>(ActivitiesLoadFailedClickEventActivitiesManifestV1.ids, {});
        reload();
    };

    return (
        <>
            {(!isError || isSuccessLoaded) && !props.isLoading && <Divider mb="24" />}
            {props.isLoading && <ReviewSectionPlaceHolder cms={cms} />}
            {isError && (
                <ContentArea
                    data-element-name="activities-review-error"
                    data-testid="activities-review-error"
                    py="16"
                    px="24"
                    display="flex"
                    justifyContent="center"
                    flexDirection="column"
                    max={60}
                    m="auto"
                >
                    <ErrorContent
                        onClick={onReloadButtonClick}
                        assetName="broken-bulb.svg"
                        assetWidth="240px"
                        buttonText={props.cms[276613]}
                        errorTitleText={props.cms[276611]}
                        errorDescriptionText={props.cms[276612]}
                    />
                </ContentArea>
            )}
            {isSuccessLoaded && <ReviewCardList reviewList={props.reviewList} isInOverlay={props.isInOverlay} />}
            {isSuccessLoaded && <ReviewPagination />}
            <Box mt="24" />
        </>
    );
};

ReviewList.displayName = 'ReviewList';
export default connect(mapStateToProps, mapDispatchToProps)(ReviewList);
